import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  Alert,
  Skeleton,
  Snackbar,
} from '@mui/material';
import { CalendarMonth, CompareArrows, CalendarToday, Add as AddIcon } from '@mui/icons-material';
import { useRenewals } from '../../hooks/useRenewals';
import { renewalsAPI } from '../../services/api';
import RenewalCard from '../../components/renewals/RenewalCard';
import InAppBanner from '../../components/renewals/InAppBanner';
import { FEATURES, REFRESH_INTERVAL } from '../../config/features';
import { sortRenewals, daysUntil, relativeTime } from '../../utils/renewalHelpers';
import { fromApiError, getErrorAction } from '../../utils/renewalErrors';
import t from '../../utils/renewalsTranslations';
import { trackRenewalsPageViewed, trackRenewalCheckStarted, trackRenewalCheckCompleted } from '../../utils/analytics';

const locale = (typeof window !== 'undefined' && localStorage.getItem('locale')) || 'en';

const FILTER_ALL = 'all';
const FILTER_EXPIRING = 'expiring_soon';
const FILTER_BETTER = 'better_deals';

export default function Renewals() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get('filter') || FILTER_ALL;
  const { upcoming, stats, loading, error, reload } = useRenewals();
  const [checking, setChecking] = useState(null);
  const [checkError, setCheckError] = useState(null);
  const [checkResult, setCheckResult] = useState(null);
  const [lastRefreshTime, setLastRefreshTime] = useState(Date.now());
  const [newDealsToast, setNewDealsToast] = useState(null);
  const prevBetterCountRef = useRef(undefined);

  const filtered = useMemo(() => {
    const sorted = sortRenewals(upcoming || []);
    if (filter === FILTER_EXPIRING) return sorted.filter((p) => (daysUntil(p.end_date) ?? 999) <= 60);
    return sorted;
  }, [upcoming, filter]);

  const expiringCount = useMemo(() => (upcoming || []).filter((p) => (daysUntil(p.end_date) ?? 999) <= 60).length, [upcoming]);
  const betterCount = stats?.active_recommendations ?? 0;

  const filterIndex = useMemo(() => {
    if (filter === FILTER_EXPIRING) return 1;
    if (filter === FILTER_BETTER) return 2;
    return 0;
  }, [filter]);
  const setFilterByIndex = (index) => {
    const v = [FILTER_ALL, FILTER_EXPIRING, FILTER_BETTER][index];
    setSearchParams(v === FILTER_ALL ? {} : { filter: v });
  };

  useEffect(() => {
    if (!loading && upcoming) {
      trackRenewalsPageViewed({ filter, policiesCount: upcoming.length, betterDealsCount: betterCount });
    }
  }, [loading, filter, upcoming?.length, betterCount]);

  useEffect(() => {
    if (loading) return;
    if (prevBetterCountRef.current !== undefined && betterCount > prevBetterCountRef.current) {
      const n = betterCount - prevBetterCountRef.current;
      setNewDealsToast(t('renewals.dashboard.newDealsFound', { count: n }) || `${n} new better deal(s) found!`);
    }
    prevBetterCountRef.current = betterCount;
  }, [loading, betterCount]);

  useEffect(() => {
    if (!FEATURES.autoRefreshDashboard) return;
    const intervalId = setInterval(() => {
      if (!document.hidden) {
        reload().then(() => {
          setLastRefreshTime(Date.now());
          if (typeof sessionStorage !== 'undefined') sessionStorage.setItem('renewals_cached_at', new Date().toISOString());
        });
      }
    }, REFRESH_INTERVAL);
    return () => clearInterval(intervalId);
  }, [reload]);

  const handleCheckNow = async (policyId) => {
    setCheckError(null);
    setCheckResult(null);
    setChecking(policyId);
    trackRenewalCheckStarted({ policyId, checkType: 'ad_hoc' });
    try {
      const result = await renewalsAPI.runCheck(policyId, 'ad_hoc');
      setCheckResult(result);
      trackRenewalCheckCompleted({
        policyId,
        alternativesFound: result.alternatives_found ?? result.alternativesFound,
        betterDealsFound: result.better_deals_found ?? result.betterDealsFound ?? 0,
        topSavings: result.potential_savings ?? result.potentialSavings,
        duration: result.check_duration_ms ?? result.checkDurationMs,
      });
      await reload();
    } catch (e) {
      setCheckError(fromApiError(e));
    } finally {
      setChecking(null);
    }
  };

  const errorInfo = error ? fromApiError({ response: { status: 500 }, message: error }) : null;

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Skeleton height={40} width={280} sx={{ mb: 2 }} />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={6} sm={3} key={i}><Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1 }} /></Grid>
          ))}
        </Grid>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={140} sx={{ borderRadius: 1 }} />
          ))}
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={reload}>{getErrorAction(errorInfo?.code) === 'retry' ? 'Retry' : 'Back'}</Button>}>
          {errorInfo?.message ?? error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">{t('renewals.dashboard.title')}</Typography>
          <Typography variant="body2" color="text.secondary">Policies expiring in the next 90 days</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {FEATURES.renewalCalendar && (
            <Button startIcon={<CalendarMonth />} onClick={() => navigate('/renewals/calendar')}>
              {t('renewals.dashboard.viewCalendar')}
            </Button>
          )}
          {FEATURES.multiPolicyComparison && (
            <Button startIcon={<CompareArrows />} onClick={() => navigate('/renewals/compare')}>
              {t('renewals.dashboard.comparePolicies')}
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}>
          <Card><CardContent>
            <Typography color="textSecondary" variant="body2">{t('renewals.dashboard.stats.expiring')}</Typography>
            <Typography variant="h6">{upcoming?.length ?? 0}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card><CardContent>
            <Typography color="textSecondary" variant="body2">{t('renewals.dashboard.stats.savings')}</Typography>
            <Typography variant="h6">—</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card><CardContent>
            <Typography color="textSecondary" variant="body2">{t('renewals.dashboard.stats.checks')}</Typography>
            <Typography variant="h6">{stats?.total_checks ?? 0}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card><CardContent>
            <Typography color="textSecondary" variant="body2">{t('renewals.dashboard.stats.recommendations')}</Typography>
            <Typography variant="h6">{betterCount}</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      {FEATURES.autoRefreshDashboard && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
          {t('renewals.dashboard.lastUpdated') || 'Last updated'}: {relativeTime(lastRefreshTime, locale)}
        </Typography>
      )}
      <Tabs value={filterIndex} onChange={(_, index) => setFilterByIndex(index)} sx={{ mb: 2 }}>
        <Tab label="All" />
        <Tab label={`Expiring Soon (${expiringCount})`} />
        <Tab label={`Better Deals (${betterCount})`} />
      </Tabs>
      <Snackbar open={!!newDealsToast} autoHideDuration={5000} onClose={() => setNewDealsToast(null)} message={newDealsToast} />

      {checkResult && (
        <InAppBanner
          type={(checkResult.better_deals_found ?? checkResult.betterDealsFound ?? 0) > 0 ? 'success' : 'info'}
          message={
            (checkResult.better_deals_found ?? checkResult.betterDealsFound ?? 0) > 0
              ? t('renewals.checkResult.betterDeals', { count: checkResult.better_deals_found ?? checkResult.betterDealsFound, amount: checkResult.potential_savings ?? checkResult.potentialSavings ?? 0 })
              : t('renewals.checkResult.noBetterDeals')
          }
          onDismiss={() => setCheckResult(null)}
          dismissible
        />
      )}
      {checkError && (
        <InAppBanner
          type={checkError.code === 'rate_limit_check' ? 'warning' : 'error'}
          message={checkError.message}
          onDismiss={() => setCheckError(null)}
          dismissible
        />
      )}

      {!error && upcoming?.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6, maxWidth: 400, mx: 'auto' }}>
          <CalendarToday sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>{t('renewals.errors.noRenewals')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('renewals.errors.noRenewalsDesc')}</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/wallet/add')}>{t('renewals.errors.addPolicy')}</Button>
        </Box>
      ) : !error && (
        <Grid container spacing={2}>
          {filtered.map((policy) => (
            <Grid item xs={12} sm={6} md={4} key={policy.id}>
              <RenewalCard policy={policy} onCheckNow={handleCheckNow} checking={checking === policy.id} />
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
}
